import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase/config";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import Header from "../components/Header";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { REPORT_FIELDS } from "../data/BOOK_REPORT_STRUCTURE";

// The five sections in order — used for the roadmap checklist
const SECTIONS = [
  "Introduction",
  "Body Paragraph 1",
  "Body Paragraph 2",
  "Body Paragraph 3",
  "Conclusion",
];

export default function BookReport() {
  const { id, reportId } = useParams();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const theme = useTheme();
  const isParent = userProfile?.role === "parent";
  const isChild = userProfile?.role === "child";

  const [book, setBook] = useState(null);
  const [report, setReport] = useState(null);

  // Local draft — what the child is currently typing
  // We keep this separate from Firestore so we don't save on every keystroke
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const fieldRefs = useRef({});

  // Listen to the book document in real time
  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "books", id), (snap) => {
      if (!snap.exists()) return;
      const bookData = { id: snap.id, ...snap.data() };
      setBook(bookData);

      // Find the specific report we're editing inside the bookReports array
      const found = (bookData.bookReports || []).find((r) => r.id === reportId);
      if (found) {
        setReport(found);
        // Only initialize the draft once (when draft is still empty)
        // After that, the child's in-progress typing takes over
        setDraft((prev) => {
          if (Object.keys(prev).length > 0) return prev;
          const initial = {};
          REPORT_FIELDS.forEach((f) => {
            initial[f.id] = found.fields?.[f.id]?.value || "";
          });
          return initial;
        });
      }
    });
    return unsubscribe;
  }, [id, reportId]);

  // Function 1 — saves current draft values to Firestore
  const saveReport = useCallback(async () => {
    if (!book || !report) return;
    setSaving(true);
    try {
      const updatedFields = { ...report.fields };
      REPORT_FIELDS.forEach((f) => {
        updatedFields[f.id] = {
          ...updatedFields[f.id],
          value: draft[f.id] || "",
        };
      });
      const updatedReports = (book.bookReports || []).map((r) =>
        r.id === reportId ? { ...r, fields: updatedFields } : r
      );
      await updateDoc(doc(db, "books", id), { bookReports: updatedReports });
    } catch {
      alert("Failed to save. Try again.");
    }
    setSaving(false);
  }, [book, report, draft, id, reportId]);

  // Function 2 — saves and flips status to "submitted"
  async function submitReport() {
    const hasAnyContent = REPORT_FIELDS.some(
      (f) => (draft[f.id] || "").trim() !== ""
    );
    if (!hasAnyContent) {
      return alert("Please fill in at least one field before submitting.");
    }
    if (!window.confirm("Submit this report for parent review? You can still edit it after submitting.")) return;
    setSaving(true);
    try {
      const updatedFields = { ...report.fields };
      REPORT_FIELDS.forEach((f) => {
        updatedFields[f.id] = {
          ...updatedFields[f.id],
          value: draft[f.id] || "",
        };
      });
      const updatedReports = (book.bookReports || []).map((r) =>
        r.id === reportId
          ? { ...r, fields: updatedFields, status: "submitted" }
          : r
      );
      await updateDoc(doc(db, "books", id), { bookReports: updatedReports });
      alert("Report submitted! Your parent will review it.");
    } catch {
      alert("Failed to submit. Try again.");
    }
    setSaving(false);
  }

  // Function 3 — parent only: toggles the locked state of a field
  async function toggleLockField(fieldId) {
    if (!isParent) return;
    const currentlyLocked = report.fields?.[fieldId]?.lockedByParent || false;
    const updatedFields = { ...report.fields };
    updatedFields[fieldId] = {
      ...updatedFields[fieldId],
      value: draft[fieldId] || "",
      lockedByParent: !currentlyLocked,
    };
    const updatedReports = (book.bookReports || []).map((r) =>
      r.id === reportId ? { ...r, fields: updatedFields } : r
    );
    try {
      await updateDoc(doc(db, "books", id), { bookReports: updatedReports });
    } catch {
      alert("Failed to update field. Try again.");
    }
  }

  if (!book || !report) {
    return (
      <div style={{ minHeight: "100vh", backgroundColor: theme.bgPage,
        display: "flex", alignItems: "center", justifyContent: "center",
        color: theme.colorText }}>
        Loading...
      </div>
    );
  }

  // ── ROADMAP CALCULATIONS ──

  // Total fields the child can actually edit (not locked by parent)
  const editableFields = REPORT_FIELDS.filter(
    (f) => !report.fields?.[f.id]?.lockedByParent
  );
  const filledCount = editableFields.filter(
    (f) => (draft[f.id] || "").trim() !== ""
  ).length;
  const totalEditable = editableFields.length;
  const overallPct = totalEditable === 0 ? 100
    : Math.round((filledCount / totalEditable) * 100);

  // Per-section completion — is every editable field in this section filled?
  function isSectionComplete(sectionName) {
    const sectionFields = REPORT_FIELDS.filter(
      (f) => f.section === sectionName && !report.fields?.[f.id]?.lockedByParent
    );
    if (sectionFields.length === 0) return true;
    return sectionFields.every((f) => (draft[f.id] || "").trim() !== "");
  }

  // Status badge colors — same pattern as ParentBookView
  const statusConfig = {
    inProgress: { bg: "#e2e8f0", color: "#4a5568", label: "✏️ In Progress" },
    submitted:  { bg: "#bee3f8", color: "#2b6cb0", label: "📬 Submitted for Review" },
    kickedBack: { bg: "#fed7d7", color: "#c53030", label: "🔁 Needs Revision" },
    reviewed:   { bg: "#c6f6d5", color: "#276749", label: "✅ Reviewed" },
  };
  const statusInfo = statusConfig[report.status] || statusConfig.inProgress;

  return (
    <div style={{ minHeight: "100vh", backgroundColor: theme.bgPage }}>
      <Header />

      <div style={{ padding: 24, maxWidth: 640, margin: "0 auto" }}>

        {/* Page title + status badge */}
        <div style={{ display: "flex", alignItems: "center",
          justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <h2 style={{ fontSize: 22, color: theme.colorText,
              margin: "0 0 4px 0", fontFamily: theme.fontBody }}>
              {report.title}
            </h2>
            <p style={{ fontSize: 14, color: theme.colorTextSoft,
              margin: 0, fontFamily: theme.fontBody }}>
              {book.title}
            </p>
          </div>
          <span style={{ fontSize: 12, fontWeight: "bold", borderRadius: 12,
            padding: "5px 12px", backgroundColor: statusInfo.bg,
            color: statusInfo.color, whiteSpace: "nowrap" }}>
            {statusInfo.label}
          </span>
        </div>

        {/* Kicked back notice */}
        {report.status === "kickedBack" && (
          <div style={{ backgroundColor: "#fff5f5", border: "1px solid #feb2b2",
            borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
            <p style={{ fontSize: 15, color: "#c53030", margin: 0,
              fontWeight: "bold", fontFamily: theme.fontBody }}>
              🔁 Your parent has sent this back for revision.
            </p>
            <p style={{ fontSize: 14, color: "#742a2a", margin: "6px 0 0 0",
              fontFamily: theme.fontBody }}>
              Look for the feedback notes below each field, make your changes, then resubmit.
            </p>
          </div>
        )}

       {/* ── ROADMAP — full outline tree ── */}
        <div style={{
          backgroundColor: isChild ? theme.bgCard : "white",
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
          border: isChild ? `2px solid ${theme.bgCardBorder}` : "none",
          boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
        }}>

          {/* Progress bar */}
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <div style={{ flex: 1, height: 10, backgroundColor: theme.bgPage,
              borderRadius: 5, border: `1px solid ${theme.bgCardBorder}` }}>
              <div style={{ height: "100%", borderRadius: 5,
                backgroundColor: theme.colorSuccess,
                width: `${overallPct}%`, transition: "width 0.3s ease" }} />
            </div>
            <span style={{ fontSize: 13, fontWeight: "bold",
              color: theme.colorTextSoft, fontFamily: theme.fontBody,
              whiteSpace: "nowrap" }}>
              {overallPct}% complete
            </span>
          </div>

          {/* Full outline tree */}
          {SECTIONS.map((section) => {
            const sectionFields = REPORT_FIELDS.filter((f) => f.section === section);
            const sectionComplete = isSectionComplete(section);

            return (
              <div key={section} style={{ marginBottom: 14 }}>

                {/* Section header row */}
                <div style={{ display: "flex", alignItems: "center",
                  gap: 8, marginBottom: 6 }}>
                  <div style={{
                    width: 12, height: 12, borderRadius: "50%", flexShrink: 0,
                    backgroundColor: sectionComplete ? theme.colorSuccess : theme.bgPage,
                    border: `2px solid ${sectionComplete
                      ? theme.colorSuccess : theme.bgCardBorder}`,
                    transition: "background-color 0.2s ease",
                  }} />
                  <span style={{ fontSize: 14, fontWeight: "bold",
                    color: sectionComplete ? theme.colorSuccess : theme.colorText,
                    fontFamily: theme.fontBody }}>
                    {section}
                  </span>
                </div>

                {/* Sub-items — each with label + prompt text */}
                {sectionFields.map((field) => {
                  const fieldData = report.fields?.[field.id] || {};
                  const isFilled = (draft[field.id] || "").trim() !== "";
                  const hasFeedback = (fieldData.parentFeedback || "").trim() !== "";
                  const isLocked = fieldData.lockedByParent;

                  const dotColor = hasFeedback ? "#d69e2e"
                    : isFilled || isLocked ? theme.colorSuccess
                    : theme.bgCardBorder;

                  const dotFill = hasFeedback ? "#fefcbf"
                    : isFilled || isLocked ? theme.colorSuccess
                    : theme.bgPage;

                  const textColor = hasFeedback ? "#d69e2e"
                    : isFilled || isLocked ? theme.colorSuccess
                    : theme.colorTextSoft;

                  return (
                    <div
                      key={field.id}
                      onClick={() => {
                        fieldRefs.current[field.id]?.scrollIntoView({
                          behavior: "smooth", block: "center"
                        });
                      }}
                      style={{
                        display: "flex", gap: 10, marginBottom: 8,
                        paddingLeft: 20, cursor: "pointer",
                        alignItems: "flex-start",
                      }}
                    >
                      {/* Dot */}
                      <div style={{
                        width: 8, height: 8, borderRadius: "50%",
                        flexShrink: 0, marginTop: 5,
                        backgroundColor: dotFill,
                        border: `2px solid ${dotColor}`,
                        transition: "all 0.2s ease",
                      }} />

                      {/* Label + prompt */}
                      <div>
                        <p style={{ fontSize: 12, fontWeight: "bold",
                          color: textColor, fontFamily: theme.fontBody,
                          margin: "0 0 2px 0" }}>
                          {field.label}{hasFeedback && " 💬"}
                        </p>
                        <p style={{ fontSize: 11, color: hasFeedback
                          ? "#d69e2e" : theme.colorTextSoft,
                          fontFamily: theme.fontBody, margin: 0,
                          lineHeight: 1.4, fontStyle: "italic" }}>
                          {field.hint}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>

        {/* ── FORM — grouped by section ── */}
        {SECTIONS.map((section) => {
          const sectionFields = REPORT_FIELDS.filter((f) => f.section === section);
          return (
            <div key={section} style={{ backgroundColor: isChild ? theme.bgCard : "white",
              borderRadius: 12, padding: 24, marginBottom: 16,
              border: isChild ? `2px solid ${theme.bgCardBorder}` : "none",
              boxShadow: isChild ? "none" : "0 2px 4px rgba(0,0,0,0.08)" }}>

              <h3 style={{ fontSize: 17, color: theme.colorText,
                margin: "0 0 18px 0", fontFamily: theme.fontBody,
                fontWeight: "bold", borderBottom: `2px solid ${theme.bgCardBorder}`,
                paddingBottom: 10 }}>
                {section}
              </h3>

              {sectionFields.map((field) => {
                const fieldData = report.fields?.[field.id] || {};
                const isLocked = fieldData.lockedByParent;
                const feedback = (fieldData.parentFeedback || "").trim();

                return (
                  <div key={field.id} style={{ marginBottom: 22 }}>

                    {/* Field label + parent lock toggle */}
                    <div style={{ display: "flex", alignItems: "center",
                      justifyContent: "space-between", marginBottom: 4 }}>
                      <label style={{ fontSize: 14, fontWeight: "bold",
                        color: theme.colorText, fontFamily: theme.fontBody }}>
                        {field.label}
                      </label>
                      {isParent && (
                        <button
                          onClick={() => toggleLockField(field.id)}
                          style={{ fontSize: 12, padding: "3px 10px",
                            borderRadius: 6, border: "1px solid",
                            cursor: "pointer", fontWeight: "bold",
                            backgroundColor: isLocked ? "#fefcbf" : "#f7fafc",
                            borderColor: isLocked ? "#f6e05e" : "#e2e8f0",
                            color: isLocked ? "#744210" : "#718096" }}>
                          {isLocked ? "🔒 Locked" : "🔓 Lock Field"}
                        </button>
                      )}
                    </div>

                    {/* Hint text */}
                    <p style={{ fontSize: 13, color: theme.colorTextSoft,
                      margin: "0 0 8px 0", fontFamily: theme.fontBody,
                      fontStyle: "italic" }}>
                      {field.hint}
                    </p>

                    {/* Locked field — show grayed out text */}
                    {isLocked && !isParent ? (
                      <div
                        ref={(el) => { fieldRefs.current[field.id] = el; }}
                        style={{ padding: "12px 14px",
                        backgroundColor: theme.bgPage, borderRadius: 8,
                        border: `1px solid ${theme.bgCardBorder}` }}>
                        <p style={{ fontSize: 13, color: theme.colorTextSoft,
                          margin: "0 0 6px 0", fontWeight: "bold",
                          fontFamily: theme.fontBody }}>
                          🔒 Set by parent
                        </p>
                        <p style={{ fontSize: 15, color: theme.colorTextSoft,
                          margin: 0, fontFamily: theme.fontBody }}>
                          {fieldData.value || "—"}
                        </p>
                      </div>
                    ) : (
                      /* Editable textarea */
                      <textarea
                        ref={(el) => { fieldRefs.current[field.id] = el; }}
                        style={{ width: "100%", padding: 12, borderRadius: 8,
                          border: `2px solid ${theme.bgCardBorder}`,
                          fontSize: 15, boxSizing: "border-box", minHeight: 80,
                          resize: "vertical", backgroundColor: theme.bgPage,
                          color: theme.colorText, fontFamily: theme.fontBody,
                          lineHeight: 1.6 }}
                        placeholder={isLocked ? "Enter your text here..." : "Write your answer here..."}
                        value={draft[field.id] || ""}
                        onChange={(e) =>
                          setDraft((prev) => ({ ...prev, [field.id]: e.target.value }))
                        }
                      />
                    )}

                    {/* Parent feedback callout — shown when feedback exists */}
                    {feedback !== "" && (
                      <div style={{ marginTop: 8, padding: "10px 14px",
                        backgroundColor: "#fffbeb",
                        border: "1px solid #f6e05e", borderRadius: 8 }}>
                        <p style={{ fontSize: 12, fontWeight: "bold",
                          color: "#744210", margin: "0 0 4px 0",
                          fontFamily: theme.fontBody }}>
                          💬 Parent feedback:
                        </p>
                        <p style={{ fontSize: 14, color: "#744210",
                          margin: 0, fontFamily: theme.fontBody }}>
                          {feedback}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* ── BOTTOM BUTTONS ── */}
        <div style={{ display: "flex", gap: 12, marginBottom: 40 }}>
          <button
            onClick={saveReport}
            disabled={saving}
            style={{ flex: 1, padding: 14, backgroundColor: theme.colorPrimary,
              color: "white", border: "none", borderRadius: 8, fontSize: 15,
              cursor: "pointer", fontWeight: "bold", fontFamily: theme.fontBody }}>
            {saving ? "Saving..." : "💾 Save"}
          </button>

          {/* Submit button — only shown to children */}
          {isChild && report.status !== "reviewed" && (
            <button
              onClick={submitReport}
              disabled={saving}
              style={{ flex: 2, padding: 14, backgroundColor: theme.colorSuccess,
                color: "white", border: "none", borderRadius: 8, fontSize: 15,
                cursor: "pointer", fontWeight: "bold", fontFamily: theme.fontBody }}>
              {report.status === "kickedBack"
                ? "🔁 Resubmit for Review"
                : "📬 Submit for Review"}
            </button>
          )}
        </div>

      </div>
    </div>
  );
}